// JoinPage role metadata.
//
// Every role has a clean URL slug (used at `/join/:slug`) and a `category`
// that maps back to the existing MENTOR_ONBOARDING_TYPES values expected by
// `/mentors/apply` — so the post-signup onboarding flow keeps working without
// backend changes.
import {
  Activity,
  Baby,
  Brain,
  ClipboardList,
  LineChart,
  Microscope,
  Repeat,
  Salad,
  ShieldCheck,
  Stethoscope,
  UserCheck,
  Users,
  Workflow,
} from './icons'

// Categories align with MENTOR_ONBOARDING_TYPES in src/consumer/constants.js.
export const JOIN_CATEGORIES = { DOCTOR: 'doctor', THERAPIST: 'therapist' }

export const JOIN_ROLES = [
  {
    slug: 'psychiatrist',
    category: JOIN_CATEGORIES.DOCTOR,
    Icon: Stethoscope,
    label: 'Psychiatrist',
    tagline:
      'Lead psychiatric diagnosis, medication management, and oversight inside a continuous clinical system.',
    seoTitle: 'Join AUMHUM as a Psychiatrist — clinical roles in continuous mental healthcare',
    seoDescription:
      'Apply to join AUMHUM as a Psychiatrist. Diagnose, prescribe, and coordinate psychiatric care inside an integrated clinical system with therapy and endocrinology teams.',
    hero: {
      eyebrow: 'Join as a Psychiatrist',
      title: 'Practice psychiatry inside a continuous clinical system.',
      lede: "Work with a coordinated team of therapists, endocrinologists, and clinical coordinators — supported by a shared clinical record, measurement-based tracking, and AI-assisted workflows designed for clinicians.",
    },
    responsibilities: [
      'Conduct structured psychiatric evaluations',
      'Prescribe and manage medication where clinically indicated',
      'Review complex cases with the multi-disciplinary team',
      'Contribute to clinical governance and peer review',
    ],
    whyJoin: [
      { Icon: Workflow, title: 'Integrated care team', body: 'Work alongside therapists, endocrinologists, and clinical coordinators on a shared plan — not in isolation.' },
      { Icon: LineChart, title: 'Measurement-based tooling', body: 'Validated instruments, outcome tracking, and AI-assisted summaries built into the daily workflow.' },
      { Icon: Repeat, title: 'Continuity by default', body: 'Patients stay with the system even when a provider changes — your plan carries forward.' },
      { Icon: ShieldCheck, title: 'Clinical supervision', body: 'Peer review, clinical governance, and safety protocols documented and enforced.' },
    ],
    requirements: [
      'MD Psychiatry (DNB / MRCPsych equivalent) or equivalent jurisdictional qualification',
      'Current registration with the Medical Council / equivalent regulator',
      'Minimum 1 year of clinical practice preferred',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'pediatrician',
    category: JOIN_CATEGORIES.DOCTOR,
    Icon: Baby,
    label: 'Pediatrician',
    tagline: 'Mental and developmental health across childhood and adolescence.',
    seoTitle: 'Join AUMHUM as a Pediatrician — child mental & developmental health',
    seoDescription:
      'Apply to join AUMHUM as a Pediatrician. Partner with child psychiatrists and psychologists on developmental, behavioural, and medical contributors to child mental health.',
    hero: {
      eyebrow: 'Join as a Pediatrician',
      title: 'Paediatric mental and developmental health — inside a coordinated team.',
      lede: 'Evaluate developmental, behavioural, and medical presentations. Partner with child psychiatrists, psychologists, and allied professionals on structured paediatric care pathways.',
    },
    responsibilities: [
      'Evaluate developmental, behavioural, and medical presentations',
      'Partner with child psychiatrists and psychologists on complex cases',
      'Support families in navigating coordinated care pathways',
      'Review medical contributors to mental health symptoms in children',
    ],
    whyJoin: [
      { Icon: Users, title: 'Multi-disciplinary team', body: 'Child psychiatrists, psychologists, speech and occupational therapists — all on one plan.' },
      { Icon: ClipboardList, title: 'Structured pathways', body: 'Evidence-informed paediatric developmental and behavioural care pathways.' },
      { Icon: Workflow, title: 'Shared clinical record', body: 'A single view of the child — across visits, providers, and settings.' },
      { Icon: ShieldCheck, title: 'Clinical supervision', body: 'Case reviews, supervision, and continuing development built-in.' },
    ],
    requirements: [
      'MD Pediatrics (DCH/DNB) or equivalent qualification',
      'Current registration with the Medical Council / equivalent regulator',
      'Experience with developmental and behavioural paediatrics is a plus',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'endocrinologist',
    category: JOIN_CATEGORIES.DOCTOR,
    Icon: Microscope,
    label: 'Endocrinologist',
    tagline: 'Treat the biological drivers of mental health — thyroid, metabolic, reproductive, sleep.',
    seoTitle: 'Join AUMHUM as an Endocrinologist — mind–body integrated care',
    seoDescription:
      'Apply to join AUMHUM as an Endocrinologist. Partner with psychiatry and therapy teams on the hormonal and metabolic drivers of mental health.',
    hero: {
      eyebrow: 'Join as an Endocrinologist',
      title: 'Mental health at the level of biology — inside a clinical team.',
      lede: 'Evaluate thyroid, metabolic, reproductive, and sleep-endocrine factors that shape mental health. Collaborate with psychiatry and therapy on combined care plans.',
    },
    responsibilities: [
      'Assess thyroid, metabolic, reproductive, and sleep-endocrine factors',
      'Partner with psychiatry on combined pharmacological planning',
      'Order and interpret relevant workups within a shared plan',
      'Review complex mind–body cases across the network',
    ],
    whyJoin: [
      { Icon: Microscope, title: 'Mind–body pathway', body: 'Structured pathway for integrating endocrine care into psychiatric treatment plans.' },
      { Icon: LineChart, title: 'Outcome tracking', body: 'Measurement across biological and psychological domains.' },
      { Icon: Workflow, title: 'Shared record', body: 'Psychiatry, therapy, and endocrinology working from one clinical view.' },
      { Icon: Users, title: 'Multi-disciplinary team', body: 'Contribute to a new standard of integrated clinical care.' },
    ],
    requirements: [
      'DM/DNB Endocrinology or equivalent specialist qualification',
      'Current registration with the Medical Council / equivalent regulator',
      'Interest in integrated mind–body care strongly preferred',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'neurologist',
    category: JOIN_CATEGORIES.DOCTOR,
    Icon: Brain,
    label: 'Neurologist',
    tagline: 'Cognitive, sleep, seizure, and movement disorders with psychiatric overlap.',
    seoTitle: 'Join AUMHUM as a Neurologist — neuro-psychiatric overlap care',
    seoDescription:
      'Apply to join AUMHUM as a Neurologist. Evaluate cognitive, sleep, seizure, and movement disorders with psychiatric overlap, in a coordinated clinical team.',
    hero: {
      eyebrow: 'Join as a Neurologist',
      title: 'Neurology within a coordinated mental healthcare team.',
      lede: 'Evaluate cognitive, sleep, and movement presentations where neurology and psychiatry intersect. Contribute neurological insight to integrated plans.',
    },
    responsibilities: [
      'Evaluate cognitive, sleep, seizure, and movement disorders with psychiatric overlap',
      'Partner with psychiatry on complex overlap cases',
      'Guide appropriate neurological workup within a shared plan',
      'Contribute to multi-disciplinary case reviews',
    ],
    whyJoin: [
      { Icon: Brain, title: 'Overlap-focused pathway', body: 'Dedicated workflows for neuro-psychiatric overlap cases.' },
      { Icon: Workflow, title: 'Shared record', body: 'One clinical view across specialists — neurology context always available.' },
      { Icon: LineChart, title: 'Measurement-based care', body: 'Validated instruments track cognitive and neuropsychiatric change over time.' },
      { Icon: Users, title: 'Peer collaboration', body: 'Case reviews with psychiatry, endocrinology, and therapy.' },
    ],
    requirements: [
      'DM/DNB Neurology or equivalent qualification',
      'Current registration with the Medical Council / equivalent regulator',
      'Experience with cognitive, sleep, or movement disorders is a plus',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'rheumatologist',
    category: JOIN_CATEGORIES.DOCTOR,
    Icon: Activity,
    label: 'Rheumatologist',
    tagline: 'Autoimmune and inflammatory drivers of mood, fatigue, and cognition.',
    seoTitle: 'Join AUMHUM as a Rheumatologist — autoimmune contributors to mental health',
    seoDescription:
      'Apply to join AUMHUM as a Rheumatologist. Collaborate on neuroimmune, rheumatological, and psychiatric overlap cases within a coordinated care system.',
    hero: {
      eyebrow: 'Join as a Rheumatologist',
      title: 'Autoimmunity meets mental healthcare.',
      lede: 'Evaluate autoimmune and inflammatory contributors to mental health. Collaborate with endocrinology, psychiatry, and therapy on integrated mind–body care plans.',
    },
    responsibilities: [
      'Assess autoimmune and inflammatory contributors to mental health',
      'Partner with endocrinology and psychiatry on combined care',
      'Review complex fatigue, pain, and cognitive presentations',
      'Contribute to multi-disciplinary case discussions',
    ],
    whyJoin: [
      { Icon: Activity, title: 'Mind–body specialisation', body: 'Rare opportunity to practice at the autoimmune / mental health boundary.' },
      { Icon: Workflow, title: 'Integrated record', body: 'One clinical view spanning rheumatology, endocrinology, and psychiatry.' },
      { Icon: LineChart, title: 'Outcome tracking', body: 'Measurement across inflammatory and psychological markers.' },
      { Icon: Users, title: 'Research exposure', body: 'Opportunity to contribute to integrated care research programs.' },
    ],
    requirements: [
      'DM/DNB Rheumatology or equivalent qualification',
      'Current registration with the Medical Council / equivalent regulator',
      'Interest in mind–body / neuroimmune presentations is a plus',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'therapist',
    category: JOIN_CATEGORIES.THERAPIST,
    Icon: Users,
    label: 'Therapist',
    tagline: 'Deliver structured therapy within a coordinated clinical care team.',
    seoTitle: 'Join AUMHUM as a Therapist — clinical therapy inside a continuous care system',
    seoDescription:
      'Apply to join AUMHUM as a Therapist. Deliver CBT, REBT, trauma-focused, and family therapy inside a clinical team with supervision and shared records.',
    hero: {
      eyebrow: 'Join as a Therapist',
      title: 'Clinical therapy, inside a care team that works together.',
      lede: 'Deliver structured therapy matched to clinical presentation, with psychiatric and medical collaboration available inside a shared record.',
    },
    responsibilities: [
      'Deliver structured therapy matched to clinical presentation',
      'Participate in supervision and clinical development',
      'Collaborate with psychiatrists and medical specialists on integrated plans',
      'Support measurement-based tracking of outcomes',
    ],
    whyJoin: [
      { Icon: ClipboardList, title: 'Structured protocols', body: 'Evidence-informed therapy pathways — CBT, REBT, trauma-focused, family work.' },
      { Icon: UserCheck, title: 'Clinical supervision', body: 'Named supervisor, case reviews, and continuing development.' },
      { Icon: Workflow, title: 'Psychiatric collaboration', body: 'Medical and psychiatric input available inside a shared clinical record.' },
      { Icon: LineChart, title: 'Outcome tracking', body: 'Measurement-based instruments built into session flow.' },
    ],
    requirements: [
      'Post-graduate / MPhil / PhD in Clinical Psychology, Counselling Psychology, or equivalent',
      'Eligibility for clinical practice under applicable jurisdiction',
      'Minimum 1 year of supervised practice preferred',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'clinical-psychologist',
    category: JOIN_CATEGORIES.THERAPIST,
    Icon: Brain,
    label: 'Clinical Psychologist',
    tagline: 'Lead structured assessment and therapy inside a clinical system.',
    seoTitle: 'Join AUMHUM as a Clinical Psychologist — structured therapy and assessment',
    seoDescription:
      'Apply to join AUMHUM as a Clinical Psychologist. Conduct assessments, formulate diagnoses, and deliver therapy within a coordinated clinical team.',
    hero: {
      eyebrow: 'Join as a Clinical Psychologist',
      title: 'Clinical psychology at its full scope.',
      lede: 'Lead clinical assessment, diagnostic formulation, and structured therapy within a care team with psychiatric and medical collaboration available when needed.',
    },
    responsibilities: [
      'Deliver structured therapy — CBT, REBT, trauma-focused, and family work',
      'Conduct clinical assessments and diagnostic formulations',
      'Collaborate with psychiatry and medical specialists on integrated plans',
      'Participate in case reviews and clinical supervision',
    ],
    whyJoin: [
      { Icon: ClipboardList, title: 'Full scope of practice', body: 'Assessment, formulation, therapy, and supervision — not just delivery.' },
      { Icon: UserCheck, title: 'Clinical supervision', body: 'Peer review and continuing development baked into the role.' },
      { Icon: Workflow, title: 'Shared clinical record', body: 'Full specialist network visible when cases require it.' },
      { Icon: LineChart, title: 'Measurement-based care', body: 'Validated instruments, outcome tracking, and AI-assisted clinical summaries.' },
    ],
    requirements: [
      'MPhil or PhD in Clinical Psychology (RCI-eligible)',
      'Current RCI / equivalent registration where applicable',
      'Minimum 1 year of post-qualification clinical practice preferred',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'counsellor',
    category: JOIN_CATEGORIES.THERAPIST,
    Icon: Users,
    label: 'Counsellor',
    tagline: 'Structured counselling inside a clinical system — triage, stabilisation, follow-up.',
    seoTitle: 'Join AUMHUM as a Counsellor — evidence-informed counselling inside a clinical team',
    seoDescription:
      'Apply to join AUMHUM as a Counsellor. Provide structured counselling support within clinical pathways, with supervision and escalation to therapy and psychiatry.',
    hero: {
      eyebrow: 'Join as a Counsellor',
      title: 'Counselling that sits inside a clinical system.',
      lede: 'Deliver structured counselling interventions — triage, stabilisation, follow-up — with supervision and clear escalation paths to therapy and psychiatry when clinically indicated.',
    },
    responsibilities: [
      'Deliver structured counselling interventions',
      'Support stabilisation and follow-up care',
      'Triage new concerns under clinical supervision',
      'Refer into therapy and psychiatric care as clinically indicated',
    ],
    whyJoin: [
      { Icon: ClipboardList, title: 'Structured pathways', body: 'Clear counselling protocols with supervision at every step.' },
      { Icon: UserCheck, title: 'Clinical supervision', body: 'Named supervisor, case reviews, and CPD hours.' },
      { Icon: Workflow, title: 'Escalation paths', body: 'Safe handoffs into therapy and psychiatric care when required.' },
      { Icon: LineChart, title: 'Progress tracking', body: 'Measurement-based monitoring of client progress.' },
    ],
    requirements: [
      'Post-graduate qualification in Counselling / Psychology or equivalent',
      'Relevant counselling experience preferred',
      'Comfortable working inside a clinical referral system',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'nutritionist',
    category: JOIN_CATEGORIES.THERAPIST,
    Icon: Salad,
    label: 'Nutritionist',
    tagline: "Nutrition's role in mental, metabolic, and endocrine health.",
    seoTitle: 'Join AUMHUM as a Nutritionist — nutrition in mental & endocrine health',
    seoDescription:
      'Apply to join AUMHUM as a Nutritionist. Partner with psychiatry, endocrinology, and therapy teams on the nutritional layer of integrated care plans.',
    hero: {
      eyebrow: 'Join as a Nutritionist',
      title: 'Nutrition inside a mental healthcare team.',
      lede: 'Address the dietary and metabolic factors that shape mood, anxiety, and endocrine health. Partner with psychiatry, endocrinology, and therapy on integrated plans.',
    },
    responsibilities: [
      'Assess nutritional and metabolic factors in mental health presentations',
      'Design nutrition plans that fit psychiatric and endocrine care',
      'Support cases involving eating patterns, metabolic conditions, and disordered eating',
      'Collaborate on complex mind–body plans',
    ],
    whyJoin: [
      { Icon: Salad, title: 'Integrated pathway', body: 'Nutrition as a first-class layer of the clinical plan — not an afterthought.' },
      { Icon: Microscope, title: 'Endocrine collaboration', body: 'Partner with endocrinologists on metabolic and hormonal drivers.' },
      { Icon: Workflow, title: 'Shared clinical record', body: 'Full medical and psychiatric context available for every client.' },
      { Icon: LineChart, title: 'Outcome tracking', body: 'Measurement across nutritional, metabolic, and psychological markers.' },
    ],
    requirements: [
      'Qualification in Nutrition / Dietetics (MSc or equivalent)',
      'Registered Dietitian / equivalent certification preferred',
      'Experience in clinical nutrition is a plus',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
  {
    slug: 'neuro-rheumatologist',
    category: JOIN_CATEGORIES.DOCTOR,
    Icon: Microscope,
    label: 'Neuro-Rheumatologist',
    tagline: 'Specialised care at the autoimmunity / neurology / mental health intersection.',
    seoTitle: 'Join AUMHUM as a Neuro-Rheumatologist — neuroimmune mental healthcare',
    seoDescription:
      'Apply to join AUMHUM as a Neuro-Rheumatologist. Work at the boundary of rheumatology, neurology, and psychiatric presentations inside an integrated clinical team.',
    hero: {
      eyebrow: 'Join as a Neuro-Rheumatologist',
      title: 'Care at the boundary of autoimmunity, neurology, and mental health.',
      lede: "A specialist role within AUMHUM's mind–body programme — evaluating inflammation, autoimmunity, and cognition where they overlap with psychiatric presentations.",
    },
    responsibilities: [
      'Evaluate neuroimmune and rheumatological contributors to mental health',
      'Partner with neurology, endocrinology, and psychiatry on complex cases',
      'Review fatigue, pain, and cognitive symptom presentations',
      'Contribute to multi-disciplinary case reviews',
    ],
    whyJoin: [
      { Icon: Microscope, title: 'Boundary-spanning role', body: 'Rare opportunity to practice at the neuroimmune / mental health boundary.' },
      { Icon: Workflow, title: 'Integrated record', body: 'Full rheumatology, neurology, endocrinology, and psychiatry context in one view.' },
      { Icon: LineChart, title: 'Outcome tracking', body: 'Cross-domain measurement and reporting.' },
      { Icon: Users, title: 'Research-aligned', body: 'Opportunity to contribute to integrated care research programs.' },
    ],
    requirements: [
      'Sub-specialty training spanning rheumatology and neurology or equivalent',
      'Current registration with the Medical Council / equivalent regulator',
      'Research interest in neuroimmunology is a strong plus',
    ],
    engagement: 'Remote, hybrid, or in-clinic. Full-time and part-time tracks supported.',
  },
]

// Legacy `type=` param mapping for the /mentors/join?type=X URLs kept alive
// for SEO. If a user lands on the old URL we redirect to the cleanest
// equivalent on the new `/join` namespace.
export const LEGACY_TYPE_TO_SLUG = {
  doctor: 'doctor', // lands on the /join/doctor category hub (see getCategoryBundle)
  therapist: 'therapist', // lands on /join/therapist (role)
  nutritionist: 'nutritionist',
  psychiatrist: 'psychiatrist',
  pediatrician: 'pediatrician',
  endocrinologist: 'endocrinologist',
  neurologist: 'neurologist',
  rheumatologist: 'rheumatologist',
  counsellor: 'counsellor',
  'clinical-psychologist': 'clinical-psychologist',
  'neuro-rheumatologist': 'neuro-rheumatologist',
}

// Category "hubs" — when the legacy URL is ?type=doctor (a category, not a
// specific role), we land the visitor on /join/doctor which lists all the
// doctor-flavoured role slugs. Same for therapist.
export const JOIN_CATEGORY_HUBS = {
  doctor: {
    slug: 'doctor',
    label: 'Doctors at AUMHUM',
    eyebrow: 'For doctors',
    tagline: 'Psychiatrists, pediatricians, endocrinologists, neurologists, and more.',
    lede: 'AUMHUM brings doctors into a coordinated clinical system where psychiatric, endocrine, and medical context flow through one clinical record. Pick the role closest to your practice to continue.',
    seoTitle: 'Doctors at AUMHUM — clinical roles in continuous mental healthcare',
    seoDescription:
      'Clinical roles for doctors at AUMHUM — psychiatrists, pediatricians, endocrinologists, neurologists, and rheumatologists inside an integrated continuous care system.',
    roleSlugs: ['psychiatrist', 'pediatrician', 'endocrinologist', 'neurologist', 'rheumatologist', 'neuro-rheumatologist'],
  },
}

export function findRoleBySlug(slug) {
  if (!slug) return null
  return JOIN_ROLES.find((r) => r.slug === slug) || null
}

export function findCategoryHub(slug) {
  if (!slug) return null
  return JOIN_CATEGORY_HUBS[slug] || null
}
